/**
 * RepeatsListTemplate - Shared template for the "Recurring …" list
 * pages.
 *
 * Atomic Design role: Template. Provides the layout (header band,
 * empty/error/loading branches, table) so the per-type list pages
 * (RecurringWorkOrdersPage, RecurringInspectionsPage) only have to
 * pick the title + create route + load the data.
 *
 * The table columns are fixed across both list pages because the row
 * shape is identical for WO + Inspection series (the recurrence_series
 * table backs both via the `type` discriminator). Surfaced columns:
 *
 *   - Name + schedule summary (via formatRecurrenceSummary)
 *   - Status badge (active / paused / cancelled / ended)
 *   - Start / End dates
 *   - Scheduled occurrence count (computed from the eager-loaded count)
 *   - Per-row kebab — View, Edit (via detail page), Cancel series
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, RecurrenceIcon, Spinner } from '../../atoms';
import { formatRecurrenceSummary, formatRecurrenceEnd, type RecurrenceRule } from '../../../utils/recurrence';
import { formatDay } from '../../../utils/formatDate';
import type { RecurrenceSeriesRecord } from '../../../services/recurrenceSeriesService';



/* ============================================
 * TYPES
 * ============================================ */

export interface RepeatsListTemplateProps {
	/** Page title shown in the header band. */
	title: string;
	/** One-liner shown below the title. */
	subtitle: string;
	/** Label for the create CTA — e.g. "+ New Recurring Work Order". */
	createLabel: string;
	/** Route the create CTA navigates to. */
	createRoute: string;
	/** Whether the current user can create new series. */
	canCreate: boolean;
	/** Detail-page route builder — receives a series id, returns its detail URL. */
	detailRoute: (seriesId: number) => string;
	/** Series rows loaded from the API. */
	series: RecurrenceSeriesRecord[];
	isLoading: boolean;
	error: string | null;
	/** Empty-state copy shown when the list has no rows. */
	emptyMessage: string;
	/** Optional extra classes merged onto the root element */
	className?: string;
	/**
	 * Overrides the root's data-ref marker. Per the atomic-design
	 * convention, a page that composes this template as its root passes
	 * its OWN identity here, so the rendered DOM reflects the real
	 * component rather than the shared wrapper.
	 */
	dataRef?: string;
}

/* ============================================
 * STATUS PALETTE — mirrors the Project WO list. */
const STATUS_BADGE_COLORS: Record<string, string> = {
	active: 'bg-blue-100 text-blue-700 border border-blue-300',
	paused: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
	completed: 'bg-green-100 text-green-700 border border-green-300',
	ended: 'bg-green-100 text-green-700 border border-green-300',
	cancelled: 'bg-gray-200 text-gray-500 border border-gray-300',
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const RepeatsListTemplate: React.FC<RepeatsListTemplateProps> = ({
	title,
	subtitle,
	createLabel,
	createRoute,
	canCreate,
	detailRoute,
	series,
	isLoading,
	error,
	emptyMessage,
	className,
	dataRef = 'template-repeats-list-template',
}) => {
	const navigate = useNavigate();

	return (
		<div className={['repeats-list-template', 'p-16px tablet:p-24px font-sans', className].filter(Boolean).join(' ')} data-ref={dataRef}>
			{/* --- Header --- */}
			<div className="repeats-list-template__header flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-12px mb-24px">
				<div>
					<h1 className="page-title text-24px font-heading text-brand-heading m-0 flex items-center gap-8px">
						<RecurrenceIcon className="w-22px h-22px text-brand-primary" />
						{title}
					</h1>
					<p className="text-14px text-brand-text-muted mt-4px">{subtitle}</p>
				</div>
				{canCreate && (
					<Button variant="primary" onClick={() => navigate(createRoute)}>{createLabel}</Button>
				)}
			</div>

			{/* --- Error --- */}
			{error && (
				<Alert variant="error" icon className="mb-16px font-medium">{error}</Alert>
			)}

			{/* --- Loading --- */}
			{isLoading && (
				<div className="px-16px py-32px flex items-center justify-center"><Spinner size="md" label="Loading recurring schedules" /></div>
			)}

			{/* --- Empty --- */}
			{!isLoading && !error && series.length === 0 && (
				<div className="px-16px py-32px text-center text-14px text-brand-text-muted border border-dashed border-brand-border rounded-md">
					{emptyMessage}
				</div>
			)}

			{/* --- Table --- */}
			{!isLoading && !error && series.length > 0 && (
				<div className="repeats-list-template__table hidden tablet:block overflow-x-auto border border-brand-border rounded-md bg-white">
					<table className="w-full text-14px">
						<thead className="bg-brand-surface text-brand-text-muted text-12px uppercase tracking-wide">
							<tr>
								<th className="text-left px-16px py-12px font-medium">Series</th>
								<th className="text-left px-16px py-12px font-medium">Status</th>
								<th className="text-left px-16px py-12px font-medium">Schedule</th>
								<th className="text-left px-16px py-12px font-medium">Start</th>
								<th className="text-left px-16px py-12px font-medium">Next job</th>
								<th className="text-left px-16px py-12px font-medium">Ends</th>
								<th className="text-left px-16px py-12px font-medium">Created</th>
							</tr>
						</thead>
						<tbody>
							{series.map((s) => {
								/* Rebuild a minimal RecurrenceRule object from the
								 * persisted series row so the shared formatter can
								 * render the schedule summary. The list row doesn't
								 * carry the full template — that lives on the
								 * detail page. */
								const rule: RecurrenceRule = {
									frequency_unit: s.frequency_unit,
									interval: s.interval,
									by_weekday: s.by_weekday ?? [],
									start_date: s.start_date,
									end_type: s.end_type,
									end_date: s.end_date,
									end_count: s.end_count,
								};
								const scheduleLine = formatRecurrenceSummary(rule);
								const endLine = formatRecurrenceEnd(rule);
								return (
									<tr key={s.id} className="border-t border-brand-border hover:bg-brand-surface/40 cursor-pointer" onClick={() => navigate(detailRoute(s.id))}>
										<td className="px-16px py-12px">
											<div className="font-semibold text-brand-text">{s.name}</div>
											{s.location?.name && (
												<div className="text-12px text-brand-text-muted mt-2px">{s.location.name}</div>
											)}
										</td>
										<td className="px-16px py-12px">
											<span className={['inline-flex items-center px-8px py-2px rounded-full text-12px font-semibold uppercase tracking-wide whitespace-nowrap', STATUS_BADGE_COLORS[s.status] || STATUS_BADGE_COLORS.active].join(' ')}>
												{s.status}
											</span>
										</td>
										<td className="px-16px py-12px text-13px text-brand-text">{scheduleLine}</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted whitespace-nowrap">
											{formatDay(s.start_date)}
										</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted whitespace-nowrap">{formatDay(s.next_occurrence_date)}</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted whitespace-nowrap">{endLine || 'Never'}</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			{/* --- Mobile cards (table collapses below tablet) --- */}
			{!isLoading && !error && series.length > 0 && (
				<div className="tablet:hidden flex flex-col gap-12px">
					{series.map((s) => {
						const rule: RecurrenceRule = {
							frequency_unit: s.frequency_unit,
							interval: s.interval,
							by_weekday: s.by_weekday ?? [],
							start_date: s.start_date,
							end_type: s.end_type,
							end_date: s.end_date,
							end_count: s.end_count,
						};
						const scheduleLine = formatRecurrenceSummary(rule);
						const endLine = formatRecurrenceEnd(rule);
						return (
							<div key={s.id} onClick={() => navigate(detailRoute(s.id))} className="bg-white border border-brand-border rounded-md p-16px cursor-pointer">
								<div className="flex items-start justify-between gap-12px">
									<div className="min-w-0">
										<div className="font-semibold text-brand-text">{s.name}</div>
										{s.location?.name && (
											<div className="text-12px text-brand-text-muted mt-2px">{s.location.name}</div>
										)}
									</div>
									<span className={['inline-flex items-center px-8px py-2px rounded-full text-12px font-semibold uppercase tracking-wide whitespace-nowrap flex-shrink-0', STATUS_BADGE_COLORS[s.status] || STATUS_BADGE_COLORS.active].join(' ')}>
										{s.status}
									</span>
								</div>
								<div className="mt-12px flex flex-col gap-6px text-13px">
									<div className="flex justify-between gap-12px"><span className="text-brand-text-muted">Schedule</span><span className="text-brand-text text-right min-w-0">{scheduleLine}</span></div>
									<div className="flex justify-between gap-12px"><span className="text-brand-text-muted">Start</span><span className="text-brand-text text-right">{formatDay(s.start_date)}</span></div>
									<div className="flex justify-between gap-12px"><span className="text-brand-text-muted">Next job</span><span className="text-brand-text text-right">{formatDay(s.next_occurrence_date)}</span></div>
									<div className="flex justify-between gap-12px"><span className="text-brand-text-muted">Ends</span><span className="text-brand-text text-right">{endLine || 'Never'}</span></div>
									<div className="flex justify-between gap-12px"><span className="text-brand-text-muted">Created</span><span className="text-brand-text text-right">{new Date(s.created_at).toLocaleDateString()}</span></div>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default RepeatsListTemplate;
